import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { PortalDashboardObject } from "@ServerTypes/portal/portal.schema";

export interface HelpSectionState {
  display: boolean | null;
  module: string;
}

// Portal dashboard modal state — purely frontend UI state
export interface PortalDashboardDataState {
  isModalVisible: boolean;
  title: string;
  component: string | null;
}

const initialPortalDashboardDataState: PortalDashboardDataState = {
  isModalVisible: false,
  title: "",
  component: null,
};

const portalDashboardDataSlice = createSlice({
  name: "portalDashboardData",
  initialState: initialPortalDashboardDataState,
  reducers: {
    setPortalData: (_state: PortalDashboardDataState, action: PayloadAction<PortalDashboardDataState>) =>
      action.payload,
    setModalVisiblity: (state: PortalDashboardDataState, action: PayloadAction<Partial<PortalDashboardDataState>>) => ({
      ...state,
      ...action.payload,
    }),
  },
});

const helpSectionStateSlice = createSlice({
  name: "helpSectionState",
  initialState: { display: null, module: "general" } as HelpSectionState,
  reducers: {
    setHelpSectionState: (_state: HelpSectionState, action: PayloadAction<HelpSectionState>) => action.payload,
  },
});

const reducerHeaderSlice = createSlice({
  name: "reducerHeader",
  initialState: true,
  reducers: {
    setReduceHeader: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const portalDashboardObjectSlice = createSlice({
  name: "portalDashboardObject",
  initialState: {} as PortalDashboardObject,
  reducers: {
    setPortalDashboardObject: (_state: PortalDashboardObject, action: PayloadAction<PortalDashboardObject>) =>
      action.payload,
  },
});

const { setPortalData: _setPortalDataRaw, setModalVisiblity: _setModalVisiblityRaw } = portalDashboardDataSlice.actions;
const { setPortalDashboardObject: _setPortalDashboardObjectRaw } = portalDashboardObjectSlice.actions;
const { setHelpSectionState: _setHelpSectionStateRaw } = helpSectionStateSlice.actions;
export const { setReduceHeader } = reducerHeaderSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setPortalData = <T extends Exact<PortalDashboardDataState, T>>(payload: T) => _setPortalDataRaw(payload);
export const setModalVisiblity = <T extends Exact<Partial<PortalDashboardDataState>, T>>(payload: T) =>
  _setModalVisiblityRaw(payload);
export const setPortalDashboardObject = <T extends Exact<PortalDashboardObject, T>>(payload: T) =>
  _setPortalDashboardObjectRaw(payload);
export const setHelpSectionState = <T extends Exact<HelpSectionState, T>>(payload: T) =>
  _setHelpSectionStateRaw(payload);

export const portalDashboardData = portalDashboardDataSlice.reducer;
export const helpSectionState = helpSectionStateSlice.reducer;
export const onReduceHeader = reducerHeaderSlice.reducer;
export const portalDashboardObject = portalDashboardObjectSlice.reducer;

export default portalDashboardDataSlice.reducer;
